import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ChefHat, Package, Wallet, BarChart2, Globe, Lock,
} from "lucide-react";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

const MODULES = [
  {
    key: "kitchen",
    label: "Carta y cocina",
    description: "Gestiona platos, recetas con escandallo y producción.",
    icon: ChefHat,
  },
  {
    key: "stock",
    label: "Almacén y compras",
    description: "Inventario, pedidos a proveedores, recepciones y previsiones IA.",
    icon: Package,
  },
  {
    key: "sales",
    label: "Ventas y caja",
    description: "Ventas en vivo, caja, cierres diarios y fidelización de clientes.",
    icon: Wallet,
  },
  {
    key: "finance",
    label: "Finanzas y análisis",
    description: "Análisis del negocio, contabilidad, IVA y libros registro.",
    icon: BarChart2,
  },
  {
    key: "digital",
    label: "Presencia digital",
    description: "Tu página web pública y la presencia en redes sociales.",
    icon: Globe,
  },
];

const FIXED_MODULES = ["home", "admin"];

/**
 * Tarjeta de configuración para activar/desactivar grupos del sidebar
 * (Configuración → General → Módulos activos).
 *
 * Onboarding gradual: por defecto se activan progresivamente según los días
 * desde la creación del restaurante. El owner puede forzar lo que quiera aquí.
 */
export default function SidebarModulesCard({ restaurantId }) {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (!restaurantId) return;
    api.request(`/restaurants/${restaurantId}/sidebar-modules`)
      .then((r) => setEnabled(new Set(r?.enabled_modules || [])))
      .catch(() => toast.error("Error cargando módulos"));
  }, [restaurantId]);

  const toggle = async (key, next) => {
    if (!enabled) return;
    setSaving((s) => ({ ...s, [key]: true }));
    const updated = new Set(enabled);
    if (next) updated.add(key); else updated.delete(key);
    // Always include the fixed modules
    FIXED_MODULES.forEach((m) => updated.add(m));
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/sidebar-modules`,
        {
          method: "PUT",
          body: JSON.stringify({ enabled_modules: Array.from(updated) }),
        },
      );
      setEnabled(new Set(res?.enabled_modules || []));
      toast.success(next ? `${MODULES.find((m) => m.key === key)?.label} activado` : `Desactivado`);
    } catch (e) {
      toast.error(e?.message || "Error al guardar");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (!enabled) {
    return (
      <div className="border rounded-xl p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando módulos…
      </div>
    );
  }

  return (
    <div className="border rounded-xl p-6 space-y-5" data-testid="sidebar-modules-card">
      <div>
        <h3 className="font-semibold text-base">Módulos del sidebar</h3>
        <p className="text-sm text-muted-foreground">
          Activa solo lo que necesitas. Los módulos se desbloquean automáticamente
          a medida que tu negocio madura, pero puedes forzar la visibilidad aquí.
        </p>
      </div>

      <div className="space-y-3">
        {MODULES.map(({ key, label, description, icon: Icon }) => {
          const isOn = enabled.has(key);
          const isSaving = saving[key];
          return (
            <div
              key={key}
              className="flex items-start justify-between gap-4 p-3 border rounded-lg"
              data-testid={`module-row-${key}`}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isOn ? "bg-indigo-100 text-indigo-700" : "bg-muted text-muted-foreground"
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{label}</p>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
              </div>
              <Switch
                checked={isOn}
                onCheckedChange={(v) => toggle(key, v)}
                disabled={isSaving}
                data-testid={`module-switch-${key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
        <Lock className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          <strong>Inicio</strong> y <strong>Configuración</strong> están siempre activos
          y no se pueden ocultar.
        </span>
      </div>
    </div>
  );
}
